import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { authApi } from '../apis/authApi';

const InvestigationGuiSliceName = 'investigationGui';

export enum RawTrackColumns {
  Time = 'Time',
  Asset = 'Asset',
  ActiveFaults = 'ActiveFaults',
  Ignition = 'Ignition',
  Speed = 'Speed',
  Heading = 'Heading',
  Forward = 'Forward',
  Reverse = 'Reverse',
  GpsFix = 'GpsFix',
  EventId = 'EventId',
  EventTypeId = 'EventTypeId',
  EventType = 'EventType',
  Latitude = 'Latitude',
  Longitude = 'Longitude',
  Altitude = 'Altitude',
  Geofences = 'Geofences',
  VehiclesInView = 'VehiclesInView',
  SatelliteCount = 'SatelliteCount',
  HDOP = 'HDOP',
  VDOP = 'VDOP',
  HorizontalAccuracy = 'HorizontalAccuracy',
  HorizontalAccuracySecondary = 'HorizontalAccuracySecondary',
  VerticalAccuracy = 'VerticalAccuracy',
  GpsAntenna = 'GpsAntenna',
  RuleName = 'RuleName',
  Audible = 'Audible',
  OtherVehicleDistance = 'OtherVehicleDistance',
  InnerBeamLength = 'InnerBeamLength', // Alarm
  OuterBeamLength = 'OuterBeamLength', // Warn
  MotiveState = 'MotiveState',
  Payload = 'Payload', // Percent
  Gradient = 'Gradient', // Percent
  Level9Active = 'Level9Active',
  Level9Action = 'Level9Action',
  Level9Level = 'Level9Level',
  OtherAssetName = 'OtherAssetName',
  OtherAssetIsDotNetix = 'OtherAssetIsDotNetix',
}

export enum InvestigationSegment {
  Map = 'map',
  Split = 'split',
  Tables = 'tables',
}

export interface InvestigationGuiState<TSiteId extends string | null> {
  siteId: TSiteId;
  view: InvestigationSegment;
  showObjectScaleMarkers: boolean;
  rawTrackCols: RawTrackColumns[];
}

// Type guard func that performs a shallow type check of the local storage data shape.
function isInvestigationGuiState(data: unknown): data is InvestigationGuiState<string> {
  return (
    data instanceof Object &&
    'siteId' in data &&
    typeof data.siteId === 'string' &&
    'view' in data &&
    typeof data.view === 'string' &&
    'showObjectScaleMarkers' in data &&
    typeof data.showObjectScaleMarkers === 'boolean' &&
    'rawTrackCols' in data &&
    Array.isArray(data.rawTrackCols)
  );
}

const getLocalStorageKey = (siteId: string) => `${InvestigationGuiSliceName}-${siteId}`;

const readFromStorage = (siteId: string): InvestigationGuiState<string> | null => {
  const key = getLocalStorageKey(siteId);
  const hasKey = Object.keys(window.localStorage).some(k => k === key);

  if (hasKey) {
    try {
      const item = window.localStorage.getItem(key);

      if (item) {
        const data: unknown = JSON.parse(item);

        // Check the data is a valid shape.
        if (data && isInvestigationGuiState(data)) {
          return data;
        }
      }
    } catch {
      // Swallow
    }
  }

  return null;
};

const writeToStorage = (state: InvestigationGuiState<string | null>) => {
  if (state.siteId) {
    const key = getLocalStorageKey(state.siteId);

    try {
      window.localStorage.setItem(key, JSON.stringify(state));
    } catch {
      // Swallow
    }
  }
};

// Default columns to show when an investigation entity is created.
export const defaultRawTrackColumns: RawTrackColumns[] = [
  RawTrackColumns.Time,
  RawTrackColumns.Asset,
  RawTrackColumns.ActiveFaults,
  RawTrackColumns.Ignition,
  RawTrackColumns.Speed,
  RawTrackColumns.Heading,
];

const defaultState: InvestigationGuiState<null | string> = {
  siteId: null,
  view: InvestigationSegment.Split,
  showObjectScaleMarkers: false,
  rawTrackCols: [...defaultRawTrackColumns],
};

const investigationGuiSlice = createSlice({
  name: InvestigationGuiSliceName,
  initialState: { ...defaultState },
  reducers: {
    setInvestigationView(state, action: PayloadAction<InvestigationSegment>) {
      state.view = action.payload;
      writeToStorage(state);
    },
    setShowObjectScaleMarkers(state, action: PayloadAction<boolean>) {
      state.showObjectScaleMarkers = action.payload;
      writeToStorage(state);
    },
    // Controls both the visibility and order of the columns. If a colId is in this list it is visible at its position in the array.
    setRawTrackCols(state, action: PayloadAction<RawTrackColumns[]>) {
      state.rawTrackCols = [...action.payload];
      writeToStorage(state);
    },
  },
  extraReducers: builder => {
    // When the auth process has succeeded, use the siteId to load the site investigationGui state from local storage.
    builder.addMatcher(authApi.endpoints.me.matchFulfilled, (state, { payload }) => {
      // Only load the state that matches the current site id from local storage.
      if (typeof payload.currentSite?.id !== 'undefined') {
        const siteId = payload.currentSite.id;
        const persistedState: InvestigationGuiState<string> | null = readFromStorage(siteId);

        // Return the persisted state if it exist, otherwise reset the state with the default state and current siteId.
        return { ...(persistedState ?? defaultState), siteId };
      }
    });
  },
});

export const { setInvestigationView, setShowObjectScaleMarkers, setRawTrackCols } =
  investigationGuiSlice.actions;

export default investigationGuiSlice.reducer;
